import type { NotePushMapping } from '../../../contracts/projects/note-push-mapping.types';
import type { NotePushExecutionPlan } from '../../../contracts/projects/requests.types';
import { canonicalizeHeading } from './canonicalize-heading';
import { buildStoredRowIndex, matchStoredTarget } from './plan-compiler';

/**
 * Compiles the canonical note-push execution plan from the verification panel's
 * CONFIRMED section→field mappings — the structured, run-local handoff that
 * replaces the ephemeral Jotai overrides which never reached execution.
 *
 * The plan carries IDENTITY + SELECTORS ONLY and NEVER clinical content: the
 * backend fetches + validates the note by `session_id` server-side, groups
 * sections sharing a target in template order, and compiles the child write
 * plan. Selectors are opaque `AxSelector` wire-JSON strings, kept verbatim.
 */

/** Flow identity + concurrency inputs the panel supplies at submit time. */
export interface NotePushPlanIdentity {
  workspaceId: string;
  sessionId: string;
  /**
   * Opaque optimistic-concurrency token: the note view's `updated_at` ISO
   * string, echoed VERBATIM (the backend compares parse-based, but the FE
   * never reformats — the token names the note version the mapping was
   * confirmed against). `null` when the FE can't source it (the backend then
   * skips the staleness gate).
   */
  noteRevision: string | null;
  /** Selectors already written+verified by a prior partial (empty on a first submit). */
  alreadyPlaced: string[];
}

// Conflict-code classification moved to the single-source-of-truth module
// `note-push-conflict-codes.ts` (G1). Import `extractNotePushPlanConflict` /
// `NotePushPlanConflict` from there.

/**
 * The single confirmed selector for a section: a user override wins over the
 * stored hint; an empty override array is a "reset" and falls back to the hint.
 * `null` when neither has a usable selector (the section is unmapped).
 *
 * The HINT lookup matches by CANONICAL heading (C3), so a heading-spelling drift
 * between the template skeleton and the recorded hint no longer reads as unmapped
 * (audit B6 — the recurring "Placed — but mapping wasn't saved"). The override
 * lookup stays keyed on the exact heading (overrides are written under the same
 * heading string the panel passes).
 *
 * ONE selector per section: a source carrying MORE than one target is INVALID,
 * not silently reduced to its first entry — such a section reads as unmapped so
 * `isPlanComplete` fails and the panel routes to discovery (the shared compiler
 * classifies it `multi_target`), never leaking a partial mapping into the plan.
 */
export const selectorForSection = (
  mapping: NotePushMapping,
  heading: string
): string | null => {
  const override = mapping.overrides[heading];
  if (override && override.length > 0) {
    return override.length === 1 && override[0] ? override[0] : null;
  }
  const match = matchStoredTarget(heading, buildStoredRowIndex(mapping.hints));
  return match.status === 'mapped' ? match.target : null;
};

/**
 * Whether every template section has a confirmed selector — the completeness
 * gate. The backend rejects incomplete plans, so `confirm/apply` is offered only
 * when this is true; an incomplete mapping routes to discovery instead. A mapping
 * with no template sections has nothing to confirm and is therefore incomplete.
 *
 * The plan is built from `templateSections`, but the panel DISPLAYS the union of
 * template sections and hint-only headings. A hint-only row would be shown then
 * silently dropped from `note_push_plan`, so a mapping with any hint heading
 * outside the template skeleton is incomplete. The skeleton membership check is
 * CANONICAL (C3): a hint that merely drifts in spelling from a template heading
 * is the same section, so it must not read as an extra out-of-skeleton row.
 */
export const isPlanComplete = (mapping: NotePushMapping): boolean => {
  const templateCanonical = new Set(mapping.templateSections.map(canonicalizeHeading));
  return (
    mapping.templateSections.length > 0 &&
    mapping.templateSections.every(
      (heading) => selectorForSection(mapping, heading) !== null
    ) &&
    mapping.hints.every((hint) =>
      templateCanonical.has(canonicalizeHeading(hint.sectionHeading))
    )
  );
};

/**
 * Compile the plan, or `null` when the mapping is incomplete (which blocks the
 * submit — the FE must never produce a request the backend will 400). Placements
 * follow TEMPLATE ORDER, exactly one selector per section; sections MAY share a
 * selector.
 */
export const buildNotePushPlan = (
  mapping: NotePushMapping,
  identity: NotePushPlanIdentity
): NotePushExecutionPlan | null => {
  if (!isPlanComplete(mapping)) return null;
  const placements = mapping.templateSections.map((heading) => ({
    section_heading: heading,
    // Non-null by `isPlanComplete`; `?? ''` keeps the type honest without a cast.
    selector: selectorForSection(mapping, heading) ?? '',
  }));
  return {
    workspace_id: identity.workspaceId,
    session_id: identity.sessionId,
    app_name: mapping.appName,
    template_id: mapping.templateId || null,
    note_revision: identity.noteRevision,
    placements,
    already_placed_selectors: identity.alreadyPlaced,
  };
};
